import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Coins, Flame, PiggyBank, ShoppingBag, Phone, Star, X } from '../components/CustomIcons';

const REDEMPTION_TABS = ['Commissary', 'Call Time', 'Premium Content', 'Reentry Savings'];
const LEVEL_TITLES = ['','Newcomer','Explorer','Student','Achiever','Scholar','Specialist','Dedicated Learner','Expert','Master','Trailblazer'];

function CreditFloat({ credits, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 1600); return () => clearTimeout(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div style={{ position: 'fixed', top: '30%', left: '50%', transform: 'translateX(-50%)', zIndex: 1000, pointerEvents: 'none' }}>
      <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '28px', color: 'var(--orange)' }}>Redeemed!</div>
    </div>
  );
}

export default function Rewards() {
  const { user, authFetch } = useAuth();
  const [activeTab, setActiveTab] = useState('Commissary');
  const [catalog, setCatalog] = useState([]);
  const [history, setHistory] = useState([]);
  const [balance, setBalance] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmItem, setConfirmItem] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [floatMsg, setFloatMsg] = useState(null);
  const [savingsAmount, setSavingsAmount] = useState(100);

  useEffect(() => {
    Promise.all([
      authFetch('/rewards/catalog'),
      authFetch('/rewards/history'),
      authFetch('/rewards/balance'),
    ]).then(([cat, hist, bal]) => {
      setCatalog(cat.data);
      setHistory(hist.data);
      setBalance(bal.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRedeem = async (item) => {
    setRedeeming(true);
    try {
      const res = await authFetch('/rewards/redeem', { method: 'post', data: { item_id: item.id } });
      setBalance(prev => ({ ...prev, credits: res.data.new_balance }));
      setFloatMsg(item.name);
      setConfirmItem(null);
    } catch (e) { alert(e.response?.data?.detail || 'Redemption failed'); }
    setRedeeming(false);
  };

  const handleDepositSavings = async () => {
    try {
      const res = await authFetch('/rewards/deposit-savings', { method: 'post', data: { credits: savingsAmount } });
      setBalance(prev => ({ ...prev, credits: res.data.new_balance, reentry_savings: res.data.new_savings }));
      setFloatMsg(`$${res.data.dollars_added.toFixed(2)} added to savings`);
    } catch (e) { alert(e.response?.data?.detail || 'Transfer failed'); }
  };

  const profile = user?.profile || {};
  const credits  = balance.credits ?? profile.credits ?? 0;
  const level    = balance.level ?? profile.level ?? 1;
  const xp       = balance.xp ?? profile.xp ?? 0;
  const maxXp    = balance.max_xp ?? profile.max_xp ?? 100;
  const streak   = balance.streak_days ?? profile.streak_days ?? 0;
  const savings  = balance.reentry_savings ?? profile.reentry_savings ?? 0;

  const filteredCatalog = catalog.filter(item => {
    if (activeTab === 'Commissary')     return item.category === 'commissary';
    if (activeTab === 'Call Time')      return item.category === 'call_time';
    if (activeTab === 'Premium Content')return item.category === 'premium';
    return false;
  });

  return (
    <div style={{ padding: '28px', background: 'var(--page-bg)', minHeight: '100vh' }} data-testid="rewards-page">
      {floatMsg && <CreditFloat credits={floatMsg} onDone={() => setFloatMsg(null)} />}
      <div style={{ maxWidth: '960px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: 'var(--text-headline)', margin: '0 0 4px 0' }}>Credits & Rewards</h1>
          <p style={{ fontSize: '14px', color: 'var(--text-body)', margin: 0, fontFamily: 'Inter, sans-serif' }}>Every lesson, every quiz, every certification earns real value.</p>
        </div>

        {/* Dark hero balance card */}
        <div style={{ background: '#0B1426', borderRadius: '16px', padding: '24px', marginBottom: '16px' }} data-testid="credit-balance-hero">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '20px' }}>
            <div>
              <p className="text-section-lbl" style={{ color: 'rgba(255,255,255,0.55)', marginBottom: '6px' }}>Total Credits</p>
              <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '52px', color: '#E87722', lineHeight: 1 }}>{credits.toLocaleString()}</div>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.55)', marginTop: '6px', fontFamily: 'Inter, sans-serif' }}>Level {level} — {LEVEL_TITLES[level] || 'Explorer'}</p>
            </div>
            <div style={{ display: 'flex', gap: '20px' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: '#E87722' }}>+85</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Today</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: 'rgba(255,255,255,0.7)' }}>+420</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>This Week</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                  <Flame size={16} color="#E87722" />
                  <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '22px', color: '#E87722' }}>{streak}</span>
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>Day streak</div>
              </div>
            </div>
          </div>
          {/* XP progress */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span style={{ fontSize: '12px', color: '#E87722', fontWeight: 600, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Level {level}: {LEVEL_TITLES[level]}</span>
              <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontFamily: 'Inter, sans-serif' }}>{xp}/{maxXp} XP</span>
            </div>
            <div style={{ height: '5px', background: 'rgba(255,255,255,0.1)', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min((xp/maxXp)*100,100)}%`, background: '#E87722', borderRadius: '3px', transition: 'width 0.4s ease' }} />
            </div>
          </div>
        </div>

        {/* Reentry Savings card */}
        <div className="card" style={{ padding: '18px 20px', marginBottom: '20px', borderLeft: '3px solid var(--green)' }} data-testid="reentry-savings-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <PiggyBank size={20} color="var(--green)" />
              <div>
                <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-headline)', margin: '0 0 2px 0' }}>${savings.toFixed(2)} saved</h3>
                <p className="text-meta" style={{ margin: 0 }}>Reentry Savings Fund · Goal: $500</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select value={savingsAmount} onChange={e => setSavingsAmount(Number(e.target.value))} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', color: 'var(--text-body)', padding: '8px 10px', fontSize: '13px', outline: 'none', fontFamily: 'Inter, sans-serif', minHeight: 'var(--touch-min)' }}>
                <option value={100}>100 credits = $1.00</option>
                <option value={200}>200 credits = $2.00</option>
                <option value={500}>500 credits = $5.00</option>
                <option value={1000}>1000 credits = $10.00</option>
              </select>
              <button onClick={handleDepositSavings} style={{ background: 'var(--green)', color: '#fff', border: 'none', borderRadius: '8px', padding: '0 16px', fontSize: '13px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, cursor: 'pointer', minHeight: 'var(--touch-min)' }} data-testid="deposit-savings-btn">Deposit</button>
            </div>
          </div>
          <div style={{ height: '5px', background: 'var(--border-subtle)', borderRadius: '3px', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min((savings/500)*100,100)}%`, background: 'var(--green)', borderRadius: '3px', transition: 'width 0.4s ease' }} />
          </div>
          <p className="text-meta" style={{ marginTop: '6px' }}>100 credits = $1.00 · Converts to accessible dollars on your release date</p>
        </div>

        {/* Redemption section */}
        <div className="card" style={{ padding: 0, overflow: 'hidden', marginBottom: '20px' }}>
          {/* Tab bar */}
          <div className="tab-bar" style={{ padding: '0 20px' }}>
            {REDEMPTION_TABS.map(tab => (
              <button key={tab} className={`tab-item${activeTab === tab ? ' active' : ''}`} onClick={() => setActiveTab(tab)}
                data-testid={`rewards-tab-${tab}`}>{tab}</button>
            ))}
          </div>

          <div style={{ padding: '20px' }}>
            {activeTab === 'Reentry Savings' ? (
              <div style={{ textAlign: 'center', padding: '30px', color: 'var(--text-muted)' }}>
                <PiggyBank size={36} style={{ marginBottom: '10px', color: 'var(--green)' }} />
                <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, color: 'var(--text-body)', marginBottom: '6px' }}>Use the deposit form above to add to your reentry savings.</p>
                <p className="text-body-sm">Your savings are held securely and convert to dollars upon your release date.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: '12px' }}>
                {(loading ? Array(6).fill(null) : filteredCatalog).map((item, i) => (
                  item === null ? (
                    <div key={i} style={{ height: '100px', borderRadius: '10px', background: 'var(--border-subtle)', animation: 'pulse 1.5s ease infinite' }} />
                  ) : (
                    <div key={item.id} className="card" style={{ padding: '16px' }} data-testid={`catalog-item-${item.id}`}>
                      <h4 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, fontSize: '14px', color: 'var(--text-headline)', margin: '0 0 8px 0', lineHeight: 1.3 }}>{item.name}</h4>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <span style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '18px', color: 'var(--orange)' }}>{item.credits}</span>
                          <span className="text-meta" style={{ marginLeft: 4 }}>cr</span>
                        </div>
                        <button onClick={() => setConfirmItem(item)} disabled={credits < item.credits}
                          style={{ background: credits >= item.credits ? 'var(--orange)' : 'var(--border)', color: credits >= item.credits ? '#fff' : 'var(--text-muted)', border: 'none', borderRadius: '7px', padding: '6px 12px', fontSize: '12px', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, cursor: credits >= item.credits ? 'pointer' : 'not-allowed', minHeight: '36px' }}
                          data-testid={`redeem-${item.id}`}>
                          Redeem
                        </button>
                      </div>
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Credit History */}
        <div className="card" style={{ padding: '20px' }} data-testid="credit-history">
          <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '15px', color: 'var(--text-headline)', margin: '0 0 16px 0' }}>Credit History</h3>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }} role="table">
              <thead>
                <tr>
                  {['Date', 'Activity', 'Type', 'Credits', 'Balance'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid var(--border)' }} className="text-section-lbl">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(history.length > 0 ? history : [
                  { created_at: new Date().toISOString(), activity: 'Daily Login', type: 'earned', credits: 5, balance: credits },
                  { created_at: new Date(Date.now()-3600000).toISOString(), activity: 'Passed Quiz: Electrical Safety', type: 'earned', credits: 25, balance: credits - 5 },
                  { created_at: new Date(Date.now()-86400000).toISOString(), activity: 'Redeemed: Music Streaming', type: 'spent', credits: -25, balance: credits - 30 },
                ]).slice(0,10).map((tx, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: 'var(--text-body)' }}>{new Date(tx.created_at).toLocaleDateString()}</td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: 'var(--text-body)' }}>{tx.activity}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ background: tx.type === 'earned' ? 'var(--green-subtle)' : 'var(--danger-subtle)', color: tx.type === 'earned' ? 'var(--green-text)' : 'var(--danger)', borderRadius: '5px', padding: '2px 7px', fontSize: '11px', fontWeight: 700, fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{tx.type}</span>
                    </td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', fontWeight: 700, color: tx.type === 'earned' ? 'var(--green-text)' : 'var(--danger)' }}>{tx.type === 'earned' ? '+' : ''}{tx.credits}</td>
                    <td style={{ padding: '10px 12px', fontSize: '13px', color: 'var(--text-muted)' }}>{tx.balance?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {confirmItem && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }} onClick={() => setConfirmItem(null)}>
          <div className="card" style={{ padding: '28px', maxWidth: '360px', width: '90%', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setConfirmItem(null)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4 }} aria-label="Close"><X size={16}/></button>
            <h3 style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, fontSize: '18px', color: 'var(--text-headline)', marginBottom: '8px' }}>Confirm Redemption</h3>
            <p style={{ color: 'var(--text-body)', marginBottom: '16px', fontSize: '14px' }}>{confirmItem.name}</p>
            <div style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800, fontSize: '36px', color: 'var(--orange)', marginBottom: '20px' }}>{confirmItem.credits}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={() => setConfirmItem(null)} style={{ flex: 1, background: 'var(--surface-alt)', border: '1px solid var(--border)', borderRadius: '8px', padding: '0', fontSize: '13px', color: 'var(--text-body)', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 600, minHeight: 'var(--touch-min)' }}>Cancel</button>
              <button onClick={() => handleRedeem(confirmItem)} disabled={redeeming} style={{ flex: 1, background: 'var(--orange)', border: 'none', borderRadius: '8px', padding: '0', fontSize: '13px', color: '#fff', cursor: 'pointer', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 700, minHeight: 'var(--touch-min)', opacity: redeeming ? 0.7 : 1 }} data-testid="confirm-redeem-btn">
                {redeeming ? 'Processing…' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
